import { Controller } from "@hotwired/stimulus";
import { validateField } from "./field_validation_errors";

export default class extends Controller {
  static targets = ["input", "errorMessage"];

  static values = {
    pattern: String,
    errorMessage: String,
    patternErrorMessage: String,
  };

  connect() {
    this.form = this.inputTarget.closest("form");
    this.boundHandleFormSubmit = this.handleFormSubmit.bind(this);
    this.addEventListeners();
  }

  disconnect() {
    if (this.form) {
      this.form.removeEventListener("submit", this.boundHandleFormSubmit);
    }
  }

  get radioGroup() {
    const form = this.inputTarget.closest("form");
    if (!form) {
      return [this.inputTarget];
    }
    return Array.from(
      form.querySelectorAll(`input[type="radio"][name="${this.inputTarget.name}"]`),
    );
  }

  get checkboxGroup() {
    const form = this.inputTarget.closest("form");
    if (!form) {
      return [this.inputTarget];
    }
    return Array.from(
      form.querySelectorAll(`input[type="checkbox"][name="${this.inputTarget.name}"]`),
    );
  }

  addEventListeners() {
    if (this.inputTarget.type === "radio" || this.inputTarget.type === "checkbox") {
      this.inputTargets.forEach((input) => {
        input.addEventListener("change", this.clearGroupErrorsIfValid.bind(this));
        input.addEventListener("invalid", this.onInvalid.bind(this));
      });
    } else {
      this.inputTarget.addEventListener("input", this.clearErrorIfValid.bind(this));
      this.inputTarget.addEventListener("blur", this.onBlur.bind(this));
      this.inputTarget.addEventListener("invalid", this.onInvalid.bind(this));
    }

    if (this.form) {
      this.form.addEventListener("submit", this.boundHandleFormSubmit);
    }
  }

  handleFormSubmit(event) {
    if (event.defaultPrevented) return;
    if (!this.isFieldVisible()) return;
    if (this.isSecondaryRadioOrCheckbox()) return;

    const error = this.getError();
    if (error) {
      event.preventDefault();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
      this.inputTarget.setCustomValidity(error);
      this.renderError(error);
      this.inputTarget.focus();
    }
  }

  clearErrorIfValid() {
    if (!this.getError()) {
      this.inputTarget.setCustomValidity("");
      this.renderError(null);
    }
  }

  clearGroupErrorsIfValid() {
    if (!this.getError()) {
      if (this.inputTarget.type === "radio") {
        this.radioGroup.forEach((radio) => {
          radio.setCustomValidity("");
          this.renderError(null, radio.closest("[data-controller='field-validation']"));
        });
      } else if (this.inputTarget.type === "checkbox") {
        this.checkboxGroup.forEach((checkbox) => {
          checkbox.setCustomValidity("");
          this.renderError(null, checkbox.closest("[data-controller='field-validation']"));
        });
      }
    }
  }

  onBlur() {
    if (!this.isFieldVisible()) return;
    const error = this.getError();
    this.inputTarget.setCustomValidity(error || "");
    this.renderError(error);
  }

  onInvalid(event) {
    event.preventDefault();
    const error = this.getError() || this.inputTarget.validationMessage;
    this.renderError(error);
  }

  getError() {
    return validateField(this.inputTarget, this.errorMessageValue, this.patternErrorMessageValue);
  }

  isSecondaryRadioOrCheckbox() {
    return (
      (this.inputTarget.type === "radio" && this.radioGroup[0] !== this.inputTarget) ||
      (this.inputTarget.type === "checkbox" && this.checkboxGroup[0] !== this.inputTarget)
    );
  }

  isFieldVisible() {
    let el = this.inputTarget;
    while (el && el !== document.body) {
      if (el.classList?.contains("hidden")) return false;
      const style = window.getComputedStyle(el);
      if (style.display === "none" || style.visibility === "hidden") return false;
      el = el.parentElement;
    }
    return true;
  }

  renderError(errorMessage, element = this.element) {
    const hasError = !!errorMessage;
    const errorTarget = element.querySelector("[data-field-validation-target='errorMessage']");
    element.classList.toggle("field-error", hasError);
    if (errorMessage && errorTarget) errorTarget.textContent = errorMessage;
  }
}
